import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, Upload, Download, Pencil, Trash2, MoreHorizontal, Settings2, Users, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PageHeader, DataTable, StatCard, StatCardGroup } from '@/components/shared'
import { emailContactService } from '@/services/emailContactService'
import { emailCustomFieldService } from '@/services/emailCustomFieldService'
import ContactFormDialog from '@/components/MarketingEmail/ContactFormDialog'

const consentVariant = {
  SUBSCRIBED: 'default',
  PENDING: 'secondary',
  UNSUBSCRIBED: 'outline',
}

const Contacts = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [deletingContact, setDeletingContact] = useState(null)
  const [rowSelection, setRowSelection] = useState({})
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [editLoadingId, setEditLoadingId] = useState(null)

  const { data, isLoading } = useQuery('email-contacts', () => emailContactService.getAllContacts(), {
    onError: () => toast.error('Failed to fetch contacts'),
  })
  const { data: fieldsData } = useQuery('email-custom-fields', emailCustomFieldService.getAllFields)

  const contacts = data?.data?.contacts || []
  const customFieldDefinitions = fieldsData?.data?.fields || []
  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id])

  const counts = useMemo(() => ({
    total: contacts.length,
    subscribed: contacts.filter((c) => c.consent_status === 'SUBSCRIBED').length,
    unsubscribed: contacts.filter((c) => c.consent_status === 'UNSUBSCRIBED').length,
  }), [contacts])

  const refresh = () => queryClient.invalidateQueries('email-contacts')

  const handleEdit = async (contact) => {
    // The list row doesn't carry custom field values (getAllContacts omits them for
    // performance); fetch the full contact so the dialog shows the same data as Contact Detail.
    setEditLoadingId(contact.id)
    try {
      const response = await emailContactService.getContactById(contact.id)
      setEditingContact(response.data.contact)
      setFormOpen(true)
    } catch (error) {
      toast.error('Failed to load contact details')
    } finally {
      setEditLoadingId(null)
    }
  }

  const handleAdd = () => {
    setEditingContact(null)
    setFormOpen(true)
  }

  const handleDelete = async () => {
    if (!deletingContact) return
    try {
      await emailContactService.deleteContacts([deletingContact.id])
      toast.success('Contact deleted')
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete contact')
    } finally {
      setDeletingContact(null)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return
    setBulkDeleting(true)
    try {
      await emailContactService.deleteContacts(selectedIds)
      toast.success(`${selectedIds.length} contact${selectedIds.length === 1 ? '' : 's'} deleted`)
      setRowSelection({})
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete contacts')
    } finally {
      setBulkDeleting(false)
      setBulkDeleteOpen(false)
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.original.email}</span>
      ),
    },
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => [row.first_name, row.last_name].filter(Boolean).join(' '),
      cell: ({ row }) => [row.original.first_name, row.original.last_name].filter(Boolean).join(' ') || '—',
    },
    {
      accessorKey: 'company_name',
      header: 'Company',
      cell: ({ row }) => row.original.company_name || '—',
    },
    {
      accessorKey: 'consent_status',
      header: 'Consent',
      cell: ({ row }) => (
        <Badge variant={consentVariant[row.original.consent_status] || 'secondary'}>
          {row.original.consent_status}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem
              disabled={editLoadingId === row.original.id}
              onClick={() => handleEdit(row.original)}
            >
              {editLoadingId === row.original.id ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="mr-2 h-4 w-4" />
              )}
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeletingContact(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [navigate, editLoadingId])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contacts"
        description="Manage the people you send marketing emails to"
        actions={
          <>
            {selectedIds.length > 0 && (
              <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete ({selectedIds.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/contacts/fields')}>
              <Settings2 className="mr-2 h-4 w-4" />
              Custom Fields
            </Button>
            <Button variant="outline" size="sm" onClick={() => emailContactService.exportContacts()}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/contacts/import')}>
              <Upload className="mr-2 h-4 w-4" />
              Import
            </Button>
            <Button size="sm" onClick={handleAdd}>
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          </>
        }
      />

      <StatCardGroup>
        <StatCard title="Total Contacts" value={counts.total} icon={Users} contentClassName="p-4" />
        <StatCard title="Subscribed" value={counts.subscribed} valueClassName="text-emerald-600" contentClassName="p-4" />
        <StatCard title="Unsubscribed" value={counts.unsubscribed} valueClassName="text-muted-foreground" contentClassName="p-4" />
      </StatCardGroup>

      <DataTable
        columns={columns}
        data={contacts}
        loading={isLoading}
        searchPlaceholder="Search contacts..."
        emptyMessage="No contacts yet. Add one or import a CSV to get started."
        pageSize={20}
        enableRowSelection
        getRowId={(row) => String(row.id)}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        onRowClick={(contact) => navigate(`/marketing-email/contacts/${contact.id}`)}
      />

      <ContactFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        contact={editingContact}
        customFieldDefinitions={customFieldDefinitions}
        onSuccess={refresh}
      />

      <AlertDialog open={!!deletingContact} onOpenChange={() => setDeletingContact(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deletingContact?.email}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.length} Contact{selectedIds.length === 1 ? '' : 's'}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.length} selected contact{selectedIds.length === 1 ? '' : 's'}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Contacts
